import { useState, useEffect } from 'react';
import {
  IconEdit,
  IconInfoCircle,
  IconDeviceFloppy,
  IconLoader2,
  IconSelector,
  IconX,
} from '@tabler/icons-react';
import api from '../lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from '@/components/ui/input-group';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';

export function HoneyConnectPage() {
  const [agentCodes, setAgentCodes] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [newAgentCode, setNewAgentCode] = useState('');
  const [agentDetails, setAgentDetails] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    loadAgentCodes();
  }, []);

  const loadAgentCodes = async (search?: string) => {
    try {
      const url = search 
        ? `/honey-connect/agent-codes?search=${search}`
        : '/honey-connect/agent-codes';
      const res = await api.get(url);
      setAgentCodes(res.data.data || []);
    } catch (err) {
      console.error('Failed to load agent codes', err);
    }
  };

  const loadAgentDetails = async (agentCode: string) => {
    try {
      const res = await api.get(`/honey-connect/agent-details?agentCode=${agentCode}`);
      setAgentDetails(res.data.data);
    } catch (err) {
      console.error('Failed to load agent details', err);
    }
  };

  const handleAgentSelect = (agent: any) => {
    setSelectedAgent(agent);
    setNewAgentCode('');
    setError('');
    setSuccess('');
    if (agent) {
      loadAgentDetails(agent.external_id);
    } else {
      setAgentDetails(null);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    setLoading(true);

    try {
      const res = await api.post('/honey-connect/update-agent-code', {
        oldAgentCode: selectedAgent.external_id,
        newAgentCode: newAgentCode,
      });

      setSuccess(res.data.message || 'Agent code updated successfully');
      setNewAgentCode('');
      setSelectedAgent(null);
      setAgentDetails(null);
      loadAgentCodes();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update agent code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Honey Connect Agent Update"
        subtitle="Update agent codes for Honey Connect customers (Distributor: 1429)"
      />

      <MainCard>
        <form onSubmit={handleUpdate}>
          <div className="flex flex-col gap-6">
            {/* Agent Code Search */}
            <div className="space-y-2">
              <Label>Search Current Agent Code</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger
                  render={
                    <Button
                      variant="outline"
                      className="w-full justify-between font-normal h-8"
                      role="combobox"
                      aria-expanded={open}
                    />
                  }
                >
                  {selectedAgent ? (
                    <span>{selectedAgent.external_id}</span>
                  ) : (
                    <span className="text-muted-foreground">Search agent code...</span>
                  )}
                  <IconSelector className="ml-auto size-4 opacity-50" />
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--anchor-width]" align="start">
                  <Command shouldFilter={false}>
                    <CommandInput
                      placeholder="Type to search..."
                      onValueChange={(value) => {
                        if (value.length >= 2) loadAgentCodes(value);
                        else if (value.length === 0) loadAgentCodes();
                      }}
                    />
                    <CommandList>
                      <CommandEmpty>No agent codes found.</CommandEmpty>
                      <CommandGroup>
                        {agentCodes.map((option) => (
                          <CommandItem
                            key={option.external_id}
                            value={option.external_id}
                            onSelect={() => {
                              handleAgentSelect(option);
                              setOpen(false);
                            }}
                          >
                            <div>
                              <p className="font-bold">{option.external_id}</p>
                              <span className="text-xs text-muted-foreground">
                                {option.count} customer{option.count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <p className="text-xs text-muted-foreground">Type to search for existing agent codes</p>
            </div>

            {/* Agent Details */}
            {agentDetails && (
              <Card className="bg-sky-50 dark:bg-sky-950/30 border-sky-200 dark:border-sky-800">
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <IconInfoCircle className="size-5" />
                    <h4 className="text-lg font-semibold">
                      Current Agent: {selectedAgent.external_id}
                    </h4>
                  </div>
                  <p className="text-sm">
                    Customers: <strong>{agentDetails.customer_count}</strong>
                  </p>
                  {agentDetails.first_customer && (
                    <span className="text-xs text-muted-foreground block">
                      First customer: {new Date(agentDetails.first_customer).toLocaleDateString()}
                    </span>
                  )}
                  {agentDetails.last_customer && (
                    <span className="text-xs text-muted-foreground block">
                      Last customer: {new Date(agentDetails.last_customer).toLocaleDateString()}
                    </span>
                  )}
                </CardContent>
              </Card>
            )}

            {/* New Agent Code Input */}
            {selectedAgent && (
              <div className="space-y-2">
                <Label htmlFor="newAgentCode">New Agent Code</Label>
                <InputGroup className="w-full">
                  <InputGroupAddon>
                    <IconEdit className="size-4" />
                  </InputGroupAddon>
                  <InputGroupInput
                    id="newAgentCode"
                    value={newAgentCode}
                    onChange={(e) => setNewAgentCode(e.target.value)}
                    placeholder="Enter new agent code (can be existing or new)"
                    required
                  />
                </InputGroup>
                <p className="text-xs text-muted-foreground">
                  All {selectedAgent.count} customer(s) will be moved to this agent code (merges if code exists)
                </p>
              </div>
            )}

            {/* Alerts */}
            {error && (
              <Alert variant="destructive">
                <AlertDescription className="flex items-center justify-between">
                  {error}
                  <button type="button" onClick={() => setError('')} className="shrink-0 ml-2 opacity-70 hover:opacity-100">
                    <IconX className="size-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert>
                <AlertDescription className="flex items-center justify-between text-emerald-700 dark:text-emerald-400">
                  {success}
                  <button type="button" onClick={() => setSuccess('')} className="shrink-0 ml-2 opacity-70 hover:opacity-100">
                    <IconX className="size-4" />
                  </button>
                </AlertDescription>
              </Alert>
            )}

            {/* Submit Button */}
            <Button
              type="submit"
              variant="outline"
              size="lg"
              disabled={loading || !selectedAgent || !newAgentCode}
              className="w-full"
            >
              {loading ? <IconLoader2 className="size-5 animate-spin" /> : <IconDeviceFloppy className="size-5" />}
              {loading ? 'Updating...' : `Update ${selectedAgent?.count || 0} Customer(s)`}
            </Button>

            {selectedAgent && newAgentCode && (
              <Alert variant="warning">
                <AlertTitle className="font-bold">Confirm Update:</AlertTitle>
                <AlertDescription>
                  This will update {selectedAgent.count} customer(s) from agent code
                  &quot;<strong>{selectedAgent.external_id}</strong>&quot; to &quot;<strong>{newAgentCode}</strong>&quot;
                </AlertDescription>
              </Alert>
            )}
          </div>
        </form>
      </MainCard>
    </div>
  );
}
